import React, { useId } from 'react';
import PropTypes from 'prop-types';
import { HelperText } from './HelperText';
import './Form.css';

/**
 * Context that exposes the row's generated ids to descendant slot children
 * so that `Input` / `Select` (or any consumer) can wire `id`, `aria-invalid`
 * and `aria-describedby` automatically.
 *
 * Slot contract:
 *   - The row generates a stable `controlId` and a `helperId` per render.
 *   - `<label htmlFor={controlId}>` is rendered programmatically associated.
 *   - Children may either:
 *       a) read these ids from `InputRowContext` via a render prop
 *          `<InputRow>{({ controlId, helperId, error }) => ...}</InputRow>`,
 *       b) rely on the auto-injection performed below: the first form
 *          control in the row, even next to a HelperText or inside a DOM
 *          wrapper, is cloned with the right props.
 *   - When `error` is truthy, the row toggles a `.error` class on the
 *     wrapper, sets `aria-invalid="true"` and points `aria-describedby`
 *     at the helper text id (if any HelperText is provided as a sibling
 *     via the `helper` prop).
 *
 * @example
 *   // Auto-injection (single child, no manual id wiring):
 *   <InputRow label="Email" helper={<HelperText>Lo usaremos para notificaciones.</HelperText>}>
 *     <Input type="email" />
 *   </InputRow>
 *
 * @example
 *   // Render-prop form (multiple controls in one row):
 *   <InputRow label="Rol / Año" helper={<HelperText>El sistema validará el rol.</HelperText>}>
 *     {({ controlId, helperId }) => (
 *       <div style={{ display: 'flex', gap: 10 }}>
 *         <Input id={controlId} aria-describedby={helperId} variant="mono" />
 *         <Input variant="mono" aria-label="Año" />
 *       </div>
 *     )}
 *   </InputRow>
 */
export const InputRowContext = React.createContext(null);

const NATIVE_CONTROLS = new Set(['input', 'select', 'textarea']);

// A control is a native form element or a component that marks itself with
// `formControl = true` and forwards `id` / `aria-*` to one (Input, Select,
// Textarea, CodSelect).
function isControl(element) {
  const { type } = element;
  return NATIVE_CONTROLS.has(type) || Boolean(type?.formControl);
}

// Plain DOM wrappers and fragments are searched; other components are not,
// because their children are not necessarily what they render.
function isTransparent(element) {
  return typeof element.type === 'string' || element.type === React.Fragment;
}

function findControl(children) {
  let found = null;
  React.Children.forEach(children, (child) => {
    if (found || !React.isValidElement(child)) return;
    if (isControl(child)) found = child;
    else if (isTransparent(child)) found = findControl(child.props.children);
  });
  return found;
}

// HelperTexts placed next to the control instead of through the `helper` prop,
// looked up the same way as the control (through DOM wrappers and fragments).
function findHelpers(children, found = []) {
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return;
    if (child.type === HelperText) found.push(child);
    else if (isTransparent(child)) findHelpers(child.props.children, found);
  });
  return found;
}

function containsAny(children, targets) {
  let hit = false;
  React.Children.forEach(children, (child) => {
    if (hit || !React.isValidElement(child)) return;
    if (targets.has(child)) hit = true;
    else if (isTransparent(child))
      hit = containsAny(child.props.children, targets);
  });
  return hit;
}

// Returns `children` with each target element replaced by `patches.get(it)`
// applied to it, cloning only the wrappers on the way down so keys and the
// rest of the tree stay as they were.
function patchTree(children, patches) {
  const mapped = React.Children.map(children, (child) => {
    if (!React.isValidElement(child)) return child;
    const patch = patches.get(child);
    if (patch) return React.cloneElement(child, patch(child));
    if (isTransparent(child) && containsAny(child.props.children, patches)) {
      return React.cloneElement(
        child,
        undefined,
        patchTree(child.props.children, patches),
      );
    }
    return child;
  });
  // A single child stays a single child (React.Children.map returns an array).
  return React.Children.count(children) === 1 && !Array.isArray(children)
    ? mapped[0]
    : mapped;
}

export function InputRow({
  label,
  htmlFor,
  cols = 1,
  error = false,
  helper,
  children,
}) {
  const generatedId = useId();
  // The labelled element: the first form control, looked up through DOM
  // wrappers and fragments, so a row with a HelperText or a combobox menu
  // next to its input still names it. Without one, a single element child is
  // labelled as before.
  const foundControl =
    typeof children === 'function' ? null : findControl(children);
  const control =
    foundControl ??
    (typeof children !== 'function' && React.isValidElement(children)
      ? children
      : null);
  const controlId = htmlFor || control?.props.id || `inputrow-${generatedId}`;
  const helperId = `${controlId}-helper`;

  const ctx = {
    controlId,
    helperId: helper ? helperId : undefined,
    error: Boolean(error),
  };

  // If `children` is a function we treat it as a render prop. Otherwise the
  // control gets the row's `id` / `aria-*` props; an id it already has wins.
  let rendered;
  if (typeof children === 'function') {
    rendered = children(ctx);
  } else if (control) {
    // Each sibling HelperText without an id gets one and describes the
    // control, after the control's own description and the `helper` prop.
    // Only next to a found control: the single-child fallback is patched as a
    // whole, so a HelperText inside it would never receive its id. A HelperText
    // that brings its own id is wired by the consumer (for example to a second
    // control in the row), so it is left out.
    const siblingHelpers = foundControl
      ? findHelpers(children).filter((h) => !h.props.id)
      : [];
    const siblingIds = siblingHelpers.map((_h, i) => `${controlId}-desc-${i}`);
    const patches = new Map(
      siblingHelpers.map((h, i) => [h, () => ({ id: siblingIds[i] })]),
    );
    patches.set(control, (target) => {
      const describedBy =
        [
          target.props['aria-describedby'],
          helper ? helperId : null,
          ...siblingIds,
        ]
          .filter(Boolean)
          .join(' ') || undefined;
      // Only keys with a value: Input, Select and Textarea compute
      // aria-invalid from their own `error` and spread props after it, so an
      // explicit undefined here would erase it.
      const patch = { id: target.props.id || controlId };
      if (error) patch['aria-invalid'] = true;
      if (describedBy) patch['aria-describedby'] = describedBy;
      return patch;
    });
    rendered = patchTree(children, patches);
  } else {
    rendered = children;
  }

  const rowCls = ['input-row', cols === 2 ? 'cols-2' : '', error ? 'error' : '']
    .filter(Boolean)
    .join(' ');

  return (
    <InputRowContext.Provider value={ctx}>
      <div className={rowCls}>
        <label className="lbl" htmlFor={controlId}>
          {label}
        </label>
        {rendered}
        {helper
          ? React.isValidElement(helper)
            ? React.cloneElement(helper, {
                id: helper.props.id || helperId,
                kind: helper.props.kind || (error ? 'err' : helper.props.kind),
              })
            : helper
          : null}
      </div>
    </InputRowContext.Provider>
  );
}

InputRow.propTypes = {
  label: PropTypes.node.isRequired,
  /** Optional explicit id for the labelled control. Auto-generated otherwise. */
  htmlFor: PropTypes.string,
  cols: PropTypes.oneOf([1, 2]),
  /** When truthy, applies error styling and wires aria-invalid on the child. */
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  /** Optional HelperText element. When provided, `aria-describedby` is wired. */
  helper: PropTypes.node,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
};

export default InputRow;
